import React, {useState, useEffect} from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Paper,
  Divider,
  Collapse,
} from "@mui/material";
import {ClearAll as ClearIcon} from "@mui/icons-material";
import {useTitans} from "../../hooks/useFirestore";
import {FirestoreTitan} from "../../services/firebase/firestore";

export interface SessionFilters {
  titanId: string;
  minLevel: number;
  maxLevel: number;
}

interface SessionFiltersProps {
  filters: SessionFilters;
  onFiltersChange: (filters: SessionFilters) => void;
  onClearFilters: () => void;
  expanded: boolean;
}

const SessionFilters: React.FC<SessionFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  expanded,
}) => {
  const {data: titans, loading: titansLoading} = useTitans();
  const [activeTitans, setActiveTitans] = useState<FirestoreTitan[]>([]);

  // Filter active titans
  useEffect(() => {
    if (titans && titans.length > 0) {
      const active = titans.filter((titan) => titan.isActive);
      setActiveTitans(active);
    }
  }, [titans]);

  // Generate level options (1-20)
  const levelOptions = Array.from({length: 20}, (_, i) => i + 1);

  const handleTitanChange = (titanId: string) => {
    onFiltersChange({
      ...filters,
      titanId,
    });
  };

  const handleLevelChange = (type: "min" | "max", value: number) => {
    onFiltersChange({
      ...filters,
      [type === "min" ? "minLevel" : "maxLevel"]: value,
    });
  };

  const hasActiveFilters =
    filters.titanId !== "" || filters.minLevel !== 1 || filters.maxLevel !== 20;

  return (
    <Collapse in={expanded}>
      <Paper sx={{p: 2, mb: 2}}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* World Filter */}
          <FormControl sx={{minWidth: 200}}>
            <InputLabel id="titan-filter-label">World</InputLabel>
            <Select
              labelId="titan-filter-label"
              value={filters.titanId}
              label="World"
              onChange={(e) => handleTitanChange(e.target.value)}
              disabled={titansLoading}
            >
              <MenuItem value="">
                <em>All Worlds</em>
              </MenuItem>
              {activeTitans.map((titan) => (
                <MenuItem key={titan.id} value={titan.id}>
                  {titan.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Level Range Filters */}
          <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
            <Typography variant="body2" color="textSecondary">
              Level Range:
            </Typography>
            <FormControl sx={{minWidth: 80}}>
              <InputLabel id="min-level-label">Min</InputLabel>
              <Select
                labelId="min-level-label"
                value={filters.minLevel}
                label="Min"
                onChange={(e) =>
                  handleLevelChange("min", Number(e.target.value))
                }
                size="small"
              >
                {levelOptions.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="textSecondary">
              to
            </Typography>
            <FormControl sx={{minWidth: 80}}>
              <InputLabel id="max-level-label">Max</InputLabel>
              <Select
                labelId="max-level-label"
                value={filters.maxLevel}
                label="Max"
                onChange={(e) =>
                  handleLevelChange("max", Number(e.target.value))
                }
                size="small"
              >
                {levelOptions.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Clear Filters Button */}
          <Button
            onClick={onClearFilters}
            disabled={!hasActiveFilters}
            startIcon={<ClearIcon />}
            variant="outlined"
            size="small"
            sx={{ml: "auto"}}
          >
            Clear Filters
          </Button>
        </Box>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <>
            <Divider sx={{my: 2}} />
            <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
              <Typography variant="body2" color="textSecondary">
                Active filters:
              </Typography>
              {filters.titanId && (
                <Typography
                  variant="body2"
                  sx={{
                    backgroundColor: "var(--sub)",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  World:{" "}
                  {activeTitans.find((t) => t.id === filters.titanId)?.name}
                </Typography>
              )}
              {(filters.minLevel !== 1 || filters.maxLevel !== 20) && (
                <Typography
                  variant="body2"
                  sx={{
                    backgroundColor: "var(--sub)",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  Level: {filters.minLevel}-{filters.maxLevel}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Collapse>
  );
};

export default SessionFilters;
